import type { PickerFloor, PickerRoom } from "@nodus/ui";

import type { FloorplanFloor, FloorplanRoom } from "./types";

/**
 * What the viewer's room finder (`@nodus/ui` `LocationPicker`, #472) lists,
 * from what the viewer already has: the plan's linked floors (one per
 * address on this level, each with its drawn region) and the rooms drawn on
 * it. Floors are grouped under their address, rooms under their floor.
 *
 * The viewer's rooms carry only floor and address *names*; each is matched
 * to its linked floor by both. A room whose floor isn't linked to the plan
 * still groups by its names, under an id from a range no database id reaches
 * (`SYNTHETIC_ID_BASE`): picking that floor has no region, so it fits the plan.
 */

/** Ids at or above this one are made up here, never a row's. */
export const SYNTHETIC_ID_BASE = 2_000_000_000;

export interface ViewerLocationData {
  floors: PickerFloor[];
  rooms: PickerRoom[];
}

const pairKey = (addressName: string, floorName: string) => `${addressName}\u0000${floorName}`;

export function viewerLocationData(
  rooms: readonly FloorplanRoom[],
  floors: readonly FloorplanFloor[] | undefined,
): ViewerLocationData {
  let next = SYNTHETIC_ID_BASE;
  const addressIds = new Map<string, number>();
  const addressId = (name: string, known?: number) => {
    if (known != null) {
      if (!addressIds.has(name)) addressIds.set(name, known);
      return known;
    }
    if (!addressIds.has(name)) addressIds.set(name, next++);
    return addressIds.get(name)!;
  };
  // Real ids first, so a name seen on a linked floor keeps its real id.
  for (const f of floors ?? []) if (f.addressId != null) addressId(f.addressName, f.addressId);

  const pickerFloors: PickerFloor[] = [];
  const floorByPair = new Map<string, PickerFloor>();
  for (const f of floors ?? []) {
    const floor: PickerFloor = {
      id: f.floorId,
      name: f.floorName,
      address: { id: addressId(f.addressName, f.addressId), name: f.addressName },
    };
    pickerFloors.push(floor);
    if (!floorByPair.has(pairKey(f.addressName, f.floorName))) floorByPair.set(pairKey(f.addressName, f.floorName), floor);
  }

  const pickerRooms: PickerRoom[] = [];
  for (const r of rooms) {
    // Only rooms drawn on the plan can be found on it.
    if (!r.polygon?.length || !r.polygon[0]?.length) continue;
    const key = pairKey(r.addressName ?? "", r.floorName ?? "");
    let floor = floorByPair.get(key);
    if (!floor && (r.floorName || r.addressName)) {
      floor = { id: next++, name: r.floorName ?? "", address: { id: addressId(r.addressName ?? ""), name: r.addressName ?? "" } };
      floorByPair.set(key, floor);
    }
    pickerRooms.push({
      id: r.id,
      name: r.name,
      description: r.description,
      floor: floor ? { id: floor.id, name: floor.name, address: floor.address ?? null } : null,
    });
  }
  return { floors: pickerFloors, rooms: pickerRooms };
}
